import {
  APPROVAL_PUSH_MODES,
  type ApprovalPushMode,
  type NotificationSettings,
  PUSH_CATEGORIES,
  PUSH_CATEGORY_SETTING,
  PUSH_MAIL_MODES,
  type PushCategory,
  type PushConfigDto,
  type PushMailMode,
  type PushSubscriptionDto,
  pushMailModeOf,
  quietHoursProblem,
} from '@fos/shared/contracts/notifications';
import {
  APPROVAL_PUSH_HINT,
  APPROVAL_PUSH_MODE_LABELS,
  PUSH_CATEGORY_HINT,
  PUSH_CATEGORY_LABELS,
  PUSH_MAIL_MODE_HINTS,
  PUSH_MAIL_MODE_LABELS,
  PUSH_SIMULATION_NOTE,
  PUSH_UNAVAILABLE_MESSAGE,
} from '@fos/shared/labels';
import { BellOff, BellRing, Monitor, Send, Smartphone } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { errorMessage, validationIssues } from '../../../lib/api';
import { formatDateShort, formatRelativePast, plural } from '../../../lib/format';
import { trpc } from '../../../lib/trpc';
import { ContextBanner } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { Checkbox } from '../../../ui/Checkbox';
import { TimeInput } from '../../../ui/DateTimeInput';
import { ListRow } from '../../../ui/ListRow';
import { SectionHeader } from '../../../ui/PageHeader';
import { Section } from '../../../ui/Section';
import { Segmented } from '../../../ui/Segmented';
import { EmptyState, ErrorState, ListSkeleton, Skeleton } from '../../../ui/States';
import { Status } from '../../../ui/Status';
import { toast } from '../../../ui/Toast';
import { InstallAppSection } from '../mobile/InstallAppSection';
import { SettingsHeader } from '../pages/SettingsLayout';
import { isMobileLabel } from './push-browser';
import { type PushDevice, type PushDeviceState, usePushDevice } from './usePushDevice';

/**
 * Ustawienia → Powiadomienia (MVP §14, DECISIONS: PWA + Web Push, cisza 21:00–8:00, Critical przełamuje).
 * Czeka na projekt w Figmie — układ jak Bezpieczeństwo: to urządzenie, lista urządzeń, kiedy powiadamiać.
 * Pilne alarmy zawsze push, akceptacje wg ustawienia, „do wiadomości” tylko w Inboxie. 17.10.2026 (DECISIONS [P]
 * „Push »że coś wpadło«”): kategorie zdarzeń „Zwykłe” — „Nowa poczta”, „Zadania przypisane do mnie i komentarze”,
 * „Zgłoszenia od klientów” (domyślnie włączone), „Wyślij testowe powiadomienie” i wskazówka, gdy telefon nie ma zgody.
 */
export function NotificationsSettingsPage() {
  const config = trpc.notifications.pushConfig.useQuery();
  const devices = trpc.notifications.devices.useQuery();
  const device = usePushDevice(config.data, devices.data);

  return (
    <div>
      <SettingsHeader
        title="Powiadomienia"
        description="Na telefonie i komputerze: pilne alarmy, akceptacje i to, co wpadło do Twojego Inboxu."
      />
      {config.data?.simulated && config.data.available && (
        <ContextBanner tone="info" className="mt-6">
          Tryb symulacji — powiadomienia nie wychodzą do urządzeń.
        </ContextBanner>
      )}
      <ThisDeviceSection config={config} device={device} />
      <InstallAppSection />
      <DevicesSection config={config.data} devices={devices} device={device} />
      <WhenSection />
    </div>
  );
}

/** Stan zapytania potrzebny sekcjom (ładowanie, błąd z ponowieniem, dane). */
interface QueryState<T> {
  data: T | undefined;
  isPending: boolean;
  isError: boolean;
  isFetching: boolean;
  refetch: () => unknown;
}

// ── To urządzenie ───────────────────────────────────────────────────────────

const DEVICE_STATE_TEXT: Record<Exclude<PushDeviceState, 'checking'>, string> = {
  on: 'Włączone — pilne alarmy, akceptacje i nowości z Inboxu przyjdą tutaj',
  off: 'Wyłączone na tym urządzeniu',
  denied: 'Zablokowane w przeglądarce — zezwól na powiadomienia w ustawieniach strony i odśwież',
  unsupported: 'Ta przeglądarka nie obsługuje powiadomień push',
  ios_needs_install: 'Na iPhonie dodaj aplikację do ekranu początkowego i otwórz ją stamtąd',
  no_service_worker:
    'Powiadomienia działają w zainstalowanej aplikacji (wersja produkcyjna), nie w trybie deweloperskim',
};

function ThisDeviceSection({ config, device }: { config: QueryState<PushConfigDto>; device: PushDevice }) {
  const Icon = isMobileLabel(device.label) ? Smartphone : Monitor;
  let body: ReactNode;
  if (config.isPending || device.state === 'checking') {
    body = <ListSkeleton rows={1} />;
  } else if (config.isError) {
    body = <ErrorState compact onRetry={() => void config.refetch()} retrying={config.isFetching} />;
  } else if (!config.data?.available) {
    body = (
      <div className="flex items-start gap-3 rounded-xl bg-surface p-4">
        <BellOff size={18} strokeWidth={1.75} aria-hidden className="mt-0.5 shrink-0 text-fg-muted" />
        <div>
          <p className="text-body-s font-medium text-fg">{PUSH_UNAVAILABLE_MESSAGE}</p>
          <p className="mt-0.5 text-body-s text-fg-secondary">
            Serwer nie ma kluczy VAPID. Inbox i Akceptacje działają bez zmian.
          </p>
        </div>
      </div>
    );
  } else {
    body = (
      <div className="border-t border-line">
        <ListRow
          height="l"
          leading={<Icon size={18} strokeWidth={1.75} aria-hidden className="text-fg-muted" />}
          title={device.label}
          description={<span className="whitespace-normal">{DEVICE_STATE_TEXT[device.state]}</span>}
          trailing={
            device.state === 'on' ? (
              <>
                <Status tone="success" className="hidden md:inline-flex">
                  Włączone
                </Status>
                <Button variant="ghost" size="s" loading={device.busy} onClick={() => void device.disable()}>
                  Wyłącz
                </Button>
              </>
            ) : device.state === 'off' ? (
              <Button
                variant="primary"
                size="s"
                icon={BellRing}
                loading={device.busy}
                onClick={() => void device.enable()}
              >
                Włącz
              </Button>
            ) : undefined
          }
        />
      </div>
    );
  }
  const phone = isMobileLabel(device.label);
  return (
    <Section className="mt-6" aria-labelledby="this-device">
      <SectionHeader tone="strong" title={<span id="this-device">To urządzenie</span>} />
      {body}
      {config.data?.available && device.state === 'denied' && <PermissionHint phone={phone} />}
    </Section>
  );
}

/**
 * Wskazówka, gdy urządzenie nie ma zgody na powiadomienia (17.10.2026: „na apce mobile nie mam powiadomień push”).
 * Zgody nie da się poprosić drugi raz z poziomu strony — trzeba ją zmienić w ustawieniach telefonu albo strony.
 */
function PermissionHint({ phone }: { phone: boolean }) {
  return (
    <div role="note" className="mt-3 rounded-lg bg-warning-subtle px-4 py-3 text-body-s text-fg">
      <p className="font-medium">{phone ? 'Telefon nie ma zgody na powiadomienia' : 'Brak zgody na powiadomienia'}</p>
      {phone ? (
        <ul className="mt-1 list-disc pl-5 text-fg-secondary">
          <li>Android: Ustawienia → Aplikacje → Fravity OS (albo Chrome) → Powiadomienia → Zezwól.</li>
          <li>iPhone: Ustawienia → Powiadomienia → Fravity OS → Zezwalaj na powiadomienia.</li>
        </ul>
      ) : (
        <p className="mt-1 text-fg-secondary">
          Kliknij kłódkę obok adresu strony → Powiadomienia → Zezwól, potem odśwież stronę.
        </p>
      )}
      <p className="mt-1 text-fg-secondary">Potem wróć tutaj i włącz powiadomienia na tym urządzeniu.</p>
    </div>
  );
}

// ── Urządzenia ──────────────────────────────────────────────────────────────

function testResultToast(r: { sent: number; failed: number; removed: number; simulated: boolean }) {
  const removed = r.removed > 0 ? `Usunięto wygasłe: ${r.removed}.` : undefined;
  if (r.sent > 0) {
    toast.success(`Wysłano na ${r.sent} ${plural(r.sent, ['urządzenie', 'urządzenia', 'urządzeń'])}`, {
      description: r.simulated ? PUSH_SIMULATION_NOTE : removed,
    });
  } else if (r.failed > 0) {
    toast.error('Nie udało się wysłać', { description: 'Usługa push nie odpowiada. Spróbuj za chwilę.' });
  } else {
    toast.info('Żadne urządzenie nie odebrało powiadomienia', { description: removed ?? 'Włącz je ponownie.' });
  }
}

function DevicesSection({
  config,
  devices,
  device,
}: {
  config: PushConfigDto | undefined;
  devices: QueryState<PushSubscriptionDto[]>;
  device: PushDevice;
}) {
  const utils = trpc.useUtils();
  const remove = trpc.notifications.unsubscribe.useMutation({
    onSuccess: () => {
      toast.success('Urządzenie wyłączone');
      void utils.notifications.devices.invalidate();
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const sendTest = trpc.notifications.sendTest.useMutation({
    onSuccess: (r) => {
      testResultToast(r);
      if (r.removed > 0) void utils.notifications.devices.invalidate();
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const list: PushSubscriptionDto[] = devices.data ?? [];

  return (
    <Section className="mt-6" aria-labelledby="push-devices">
      <SectionHeader
        tone="strong"
        title={<span id="push-devices">Urządzenia z powiadomieniami</span>}
        action={
          list.length > 0 && config?.available ? (
            <Button
              variant="secondary"
              size="s"
              icon={Send}
              loading={sendTest.isPending}
              onClick={() => sendTest.mutate({})}
            >
              Wyślij testowe powiadomienie
            </Button>
          ) : undefined
        }
      />
      {devices.isPending ? (
        <ListSkeleton rows={2} />
      ) : devices.isError ? (
        <ErrorState compact onRetry={() => void devices.refetch()} retrying={devices.isFetching} />
      ) : list.length === 0 ? (
        <EmptyState compact title="Żadne urządzenie nie ma włączonych powiadomień." />
      ) : (
        <div className="border-t border-line">
          {list.map((item) => {
            const current = item.fingerprint === device.fingerprint;
            const Icon = isMobileLabel(item.deviceLabel) ? Smartphone : Monitor;
            return (
              <ListRow
                key={item.id}
                height="l"
                leading={<Icon size={18} strokeWidth={1.75} aria-hidden className="text-fg-muted" />}
                title={item.deviceLabel}
                description={[
                  `włączone ${formatDateShort(item.createdAt)}`,
                  item.lastUsedAt ? `ostatnio ${formatRelativePast(item.lastUsedAt)}` : 'jeszcze nic nie wysłano',
                ].join(' · ')}
                trailing={
                  <>
                    {current && (
                      <Status tone="success" className="hidden md:inline-flex">
                        To urządzenie
                      </Status>
                    )}
                    <Button
                      variant="ghost"
                      size="s"
                      loading={current ? device.busy : remove.isPending && remove.variables?.id === item.id}
                      onClick={() => (current ? void device.disable() : remove.mutate({ id: item.id }))}
                      aria-label={`Wyłącz: ${item.deviceLabel}`}
                    >
                      Wyłącz
                    </Button>
                  </>
                }
              />
            );
          })}
        </div>
      )}
    </Section>
  );
}

// ── Kiedy powiadamiać ───────────────────────────────────────────────────────

/** Jedno zdanie pod przełącznikiem kategorii (co dokładnie wywołuje powiadomienie). */
const PUSH_CATEGORY_DETAILS: Record<PushCategory, string> = {
  mail: 'Nowy mail w Twoim Inboxie — przypisany do Ciebie albo do klienta, którego prowadzisz.',
  tasks: 'Ktoś przypisał Ci zadanie albo skomentował Twoje zadanie. Twoje własne zmiany nie powiadamiają.',
  clientRequests: 'Nowe zgłoszenie albo wiadomość z portalu klienta (do opiekuna klienta, bez opiekuna — do adminów).',
};

const DEFAULT_QUIET = { start: '21:00', end: '08:00' };

function WhenSection() {
  const utils = trpc.useUtils();
  const settings = trpc.notifications.settings.useQuery();
  const update = trpc.notifications.updateSettings.useMutation();
  const [quietError, setQuietError] = useState<string | null>(null);
  const [draft, setDraft] = useState(DEFAULT_QUIET);
  const data = settings.data;

  useEffect(() => {
    if (data?.quietHoursStart && data.quietHoursEnd) setDraft({ start: data.quietHoursStart, end: data.quietHoursEnd });
  }, [data?.quietHoursStart, data?.quietHoursEnd]);

  const save = (patch: Partial<NotificationSettings>) => {
    setQuietError(null);
    update.mutate(patch, {
      onSuccess: (next) => {
        utils.notifications.settings.setData(undefined, next);
        toast.success('Zapisano');
      },
      onError: (err) => {
        const issue = validationIssues(err).find((i) => i.path.startsWith('quietHours'));
        if (issue) setQuietError(issue.message);
        else toast.error(errorMessage(err));
      },
    });
  };

  const saveQuiet = (next: { start: string; end: string }) => {
    setDraft(next);
    if (!next.start || !next.end) return setQuietError('Podaj początek i koniec ciszy');
    const problem = quietHoursProblem({ quietHoursStart: next.start, quietHoursEnd: next.end });
    if (problem) return setQuietError(problem.message);
    if (next.start === data?.quietHoursStart && next.end === data?.quietHoursEnd) return setQuietError(null);
    save({ quietHoursStart: next.start, quietHoursEnd: next.end });
  };

  let body: ReactNode;
  if (settings.isPending) {
    body = (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-5 w-64" />
        <Skeleton className="h-5 w-72" />
        <Skeleton className="h-8 w-80" />
      </div>
    );
  } else if (settings.isError) {
    body = <ErrorState compact onRetry={() => void settings.refetch()} retrying={settings.isFetching} />;
  } else {
    const s = settings.data;
    const quietOn = s.quietHoursStart !== null && s.quietHoursEnd !== null;
    body = (
      <div className="flex flex-col divide-y divide-line border-t border-line">
        <SettingRow hint="Wyłączone: nic nie przyjdzie na urządzenia, wszystko zostaje w Inboxie.">
          <Checkbox
            label={<span className="font-medium text-fg">Powiadomienia push</span>}
            checked={s.pushEnabled}
            disabled={update.isPending}
            onCheckedChange={(v) => save({ pushEnabled: v })}
          />
        </SettingRow>

        {/* poczta: Istotne / Wszystkie / Wyłączone (17.10.2026) — pozostałe kategorie: włącz / wyłącz */}
        <SettingRow hint={PUSH_MAIL_MODE_HINTS[pushMailModeOf(s)]}>
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <span className="text-body-s font-medium text-fg">{PUSH_CATEGORY_LABELS.mail}</span>
            <Segmented<PushMailMode>
              aria-label="Powiadomienia o nowej poczcie"
              value={pushMailModeOf(s)}
              onValueChange={(v) => save({ pushMailMode: v })}
              options={PUSH_MAIL_MODES.map((m) => ({ value: m, label: PUSH_MAIL_MODE_LABELS[m] }))}
              disabled={update.isPending || !s.pushEnabled}
              className="max-w-full"
            />
          </div>
        </SettingRow>

        {PUSH_CATEGORIES.filter((c) => c !== 'mail').map((category) => (
          <SettingRow key={category} hint={PUSH_CATEGORY_DETAILS[category]}>
            <Checkbox
              label={<span className="font-medium text-fg">{PUSH_CATEGORY_LABELS[category]}</span>}
              checked={s[PUSH_CATEGORY_SETTING[category]] !== false}
              disabled={update.isPending || !s.pushEnabled}
              onCheckedChange={(v) => save({ [PUSH_CATEGORY_SETTING[category]]: v })}
            />
          </SettingRow>
        ))}

        <SettingRow hint="W tych godzinach powiadomienia czekają do końca ciszy (czas polski).">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <Checkbox
              label={<span className="font-medium text-fg">Godziny ciszy</span>}
              checked={quietOn}
              disabled={update.isPending}
              onCheckedChange={(v) =>
                v
                  ? saveQuiet(draft.start && draft.end ? draft : DEFAULT_QUIET)
                  : save({ quietHoursStart: null, quietHoursEnd: null })
              }
            />
            {quietOn && (
              <fieldset className="flex items-center gap-2" aria-label="Godziny ciszy">
                <span className="text-body-s text-fg-secondary">od</span>
                <TimeInput
                  aria-label="Początek ciszy"
                  value={draft.start}
                  onValueChange={(v) => saveQuiet({ ...draft, start: v })}
                  aria-invalid={quietError ? true : undefined}
                />
                <span className="text-body-s text-fg-secondary">do</span>
                <TimeInput
                  aria-label="Koniec ciszy"
                  value={draft.end}
                  onValueChange={(v) => saveQuiet({ ...draft, end: v })}
                  aria-invalid={quietError ? true : undefined}
                />
              </fieldset>
            )}
          </div>
          {quietError && (
            <p role="alert" className="mt-2 text-caption text-danger">
              {quietError}
            </p>
          )}
        </SettingRow>

        <SettingRow hint="Potwierdzona awaria przyjdzie także w nocy.">
          <Checkbox
            label={<span className="font-medium text-fg">Pilne alarmy przełamują ciszę</span>}
            checked={s.criticalBreaksQuiet}
            disabled={update.isPending || !quietOn}
            onCheckedChange={(v) => save({ criticalBreaksQuiet: v })}
          />
        </SettingRow>

        <SettingRow hint={APPROVAL_PUSH_HINT}>
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <span className="text-body-s font-medium text-fg">Akceptacje</span>
            <Segmented<ApprovalPushMode>
              aria-label="Powiadomienia o akceptacjach"
              value={s.approvalPush}
              onValueChange={(v) => save({ approvalPush: v })}
              options={APPROVAL_PUSH_MODES.map((m) => ({ value: m, label: APPROVAL_PUSH_MODE_LABELS[m] }))}
              className="max-w-full"
            />
          </div>
        </SettingRow>
      </div>
    );
  }

  return (
    <Section className="mt-6" aria-labelledby="push-when">
      <SectionHeader tone="strong" title={<span id="push-when">Kiedy powiadamiać</span>} />
      {body}
      <p className="mt-4 text-caption text-fg-muted">
        {PUSH_CATEGORY_HINT} Informacje „do wiadomości” nie wysyłają powiadomień (poczta „Mniej ważne” — tylko w trybie
        „Wszystkie”). Na ekranie blokady widać tylko „Fravity OS” i rodzaj sprawy (np. „Nowe zgłoszenie od klienta”);
        szczegóły po otwarciu i zalogowaniu.
      </p>
    </Section>
  );
}

function SettingRow({ children, hint }: { children: ReactNode; hint: string }) {
  return (
    <div className="py-3.5">
      {children}
      <p className="mt-1 pl-6 text-caption text-fg-muted">{hint}</p>
    </div>
  );
}
